import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { CreditCard, Shield, CheckCircle, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UpdateCardPage = () => {
  const { slug } = useParams();
  const { toast } = useToast();

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Step 1: load subscription info
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/subscription/${slug}/update-card-info`);
        setInfo(res.data);
      } catch (err) {
        setLoadError(err.response?.data?.detail || "Unable to load subscription.");
      } finally {
        setLoadingInfo(false);
      }
    })();
  }, [slug]);

  // Step 2: init Stripe Elements once info is loaded
  useEffect(() => {
    if (!info || loadError) return;
    (async () => {
      try {
        const res = await axios.post(`${API}/subscription/${slug}/update-card-setup-intent`);
        const { client_secret, publishable_key } = res.data;
        setClientSecret(client_secret);
        if (!window.Stripe) {
          setLoadError("Stripe is not loaded. Please refresh the page.");
          return;
        }
        const stripeInstance = window.Stripe(publishable_key);
        setStripe(stripeInstance);
        const elementsInstance = stripeInstance.elements({
          clientSecret: client_secret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#e6007e",
              colorBackground: "#ffffff",
              colorText: "#1a1a1a",
              colorDanger: "#dc2626",
              fontFamily: "system-ui, sans-serif",
              borderRadius: "8px",
            },
          },
        });
        setElements(elementsInstance);
        setTimeout(() => {
          const paymentElement = elementsInstance.create("payment", { layout: "tabs" });
          const container = document.getElementById("update-card-element");
          if (container) {
            container.innerHTML = "";
            paymentElement.mount("#update-card-element");
            paymentElement.on("change", (ev) => {
              setCardComplete(ev.complete);
              setSubmitError(ev.error ? ev.error.message : null);
            });
          }
        }, 100);
      } catch (err) {
        setLoadError(err.response?.data?.detail || "Unable to initialize card update.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const handleSubmit = async () => {
    if (!stripe || !elements || !clientSecret) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (error) throw new Error(error.message);
      if (setupIntent?.status !== "succeeded") {
        throw new Error(`Card setup returned status: ${setupIntent?.status || "unknown"}`);
      }
      const res = await axios.post(`${API}/subscription/${slug}/confirm-card-update`, {
        setup_intent_id: setupIntent.id,
      });
      if (!res.data?.success) throw new Error(res.data?.message || "Unable to confirm card update");
      setSuccess(true);
      toast({ title: "Card updated", description: "Your new card is now on file." });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Card update failed.";
      setSubmitError(msg);
      toast({ title: "Card update failed", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render ---
  if (loadingInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e6007e] mx-auto mb-3"></div>
          <p className="text-gray-600 font-body">Loading your subscription…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl p-8 text-center">
          <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">We couldn't load this page</h1>
          <p className="text-gray-600 font-body mb-6">{loadError}</p>
          <Link to="/" className="text-sm text-[#e6007e] underline font-body">Back to Paify</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-10 text-center" data-testid="update-card-success">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Card updated</h1>
          <p className="text-gray-600 font-body mb-2">
            Your new card is saved and your subscription with <strong>{info.business_name}</strong> is back on track.
          </p>
          <p className="text-sm text-gray-500 font-body">You can safely close this page.</p>
        </div>
      </div>
    );
  }

  const amountStr = `${(info.currency === "GBP" ? "£" : info.currency + " ")}${Number(info.amount || 0).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Update your payment method</h1>
          <p className="text-gray-600 font-body">
            Keep your <strong>{info.title}</strong> subscription with <strong>{info.business_name}</strong> active.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5">
          <div className="flex justify-between items-center pb-4 border-b border-gray-100">
            <span className="text-sm text-gray-600 font-body">Subscription</span>
            <span className="font-semibold text-gray-900 font-heading">{info.title}</span>
          </div>
          <div className="flex justify-between items-center pt-4">
            <span className="text-sm text-gray-600 font-body">Monthly amount</span>
            <span className="font-semibold text-[#e6007e] font-heading">{amountStr}/mo</span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-gray-400" />
            <span className="font-semibold text-gray-700 font-heading">New card details</span>
          </div>
          <div id="update-card-element" className="min-h-[160px]"></div>

          {submitError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600 font-body">{submitError}</p>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={submitting || !cardComplete}
            className="w-full mt-5 h-12 bg-[#e6007e] hover:bg-[#c90068] text-white font-body disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="update-card-submit-btn"
          >
            {submitting ? "Saving your card…" : "Save new card"}
          </Button>

          <p className="text-xs text-center text-gray-500 mt-3 font-body">
            <Shield className="w-3 h-3 inline mr-1" />
            Payments are secured with bank-level encryption. Your card is stored with Stripe.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpdateCardPage;
